import {
  ContextModelNameEnum,
  DocumentType,
  isFieldErrorResponse,
  useApiDocumentsV1DocumentsCreate,
  useCurrentUser,
} from 'api';
import { useNotification } from 'features';
import {
  AutocompleteField,
  Form,
  InferType,
  InspectionAutocomplete,
  LoadAutoComplete,
  PurchaseOrderAutocomplete,
  SalesOrderAutocomplete,
  SelectField,
  ShipmentAutocomplete,
  TradeCompanyAutocomplete,
  useForm,
  yup,
  yupResolver,
} from 'form';
import { type I18nTranslateFunction, useTranslation } from 'i18n';
import { Box, Button, Page, Section } from 'ui';

import { DealAutocomplete } from '../../../deal/components/DealAutocomplete/DealAutocomplete';
import { DOCUMENT_TYPE_DISPLAYED_VALUE } from '../../../document/utils/document';

const schema = yup.object().shape({
  documentType: yup.string().oneOf(Object.values(DocumentType)).required(),
  entityId: yup.number().required(),
  modelName: yup.string().oneOf(Object.values(ContextModelNameEnum)).required(),
});

type FormValues = InferType<typeof schema>;

const DOCUMENT_TYPES_TO_EXCLUDE: Set<DocumentType> = new Set([DocumentType.CI, DocumentType.SO, DocumentType.PO]);

export const GenerateDocumentPage = () => {
  const { t } = useTranslation('orders');
  const { open } = useNotification();
  const { data: user } = useCurrentUser();
  const { mutate: generateDocument, isPending } = useApiDocumentsV1DocumentsCreate({
    mutation: {
      onError: (error) => {
        if (isFieldErrorResponse(error)) {
          Object.keys(error.response?.data).forEach((field) => {
            form.setError(field as keyof FormValues, { message: error.response.data[field] });
          });
          if (error.response?.data.non_field_errors) {
            open({
              description: error?.response?.data.non_field_errors[0],
              message: t('documentGeneration.generatePage.notifications.errorGeneratingDocument'),
              type: 'error',
            });
          }
          return;
        }
        open({ message: t('documentGeneration.generatePage.notifications.failedToGenerateDocument'), type: 'error' });
      },
      onSuccess: ({ id }) => {
        // eslint-disable-next-line react-hooks/immutability
        window.location.href = `/ground-control/document-generation/${id}`;
      },
    },
  });

  const form = useForm({
    resolver: yupResolver(schema),
  });

  const selectedModel = form.watch('modelName');

  const handleSubmit = (values: FormValues) => {
    if (!user) {
      return;
    }

    generateDocument({
      data: {
        auto_generate_pdf: true,
        context_model_name: values.modelName,
        context_object_id: values.entityId,
        created_by_id: user?.id,
        document_type: values.documentType,
      },
    });
  };

  const handleModelChange = () => {
    form.resetField('entityId');
    form.resetField('documentType');
  };

  // Convert ContextModelNameEnum to options for SelectField
  const modelOptions = Object.entries(ContextModelNameEnum).map(([key, value]) => ({
    label: key,
    value,
  }));

  // Get all document type options
  const documentTypeOptions = Object.entries(DocumentType)
    .filter(([_, value]) => !DOCUMENT_TYPES_TO_EXCLUDE.has(value))
    .map(([_, value]) => ({
      label: DOCUMENT_TYPE_DISPLAYED_VALUE[value],
      value,
    }));

  return (
    <Page>
      <Page.Title title={t('documentGeneration.generatePage.title')} />
      <Section>
        <Form {...form} onSubmit={handleSubmit}>
          <SelectField
            name="modelName"
            label={t('documentGeneration.generatePage.labels.selectModel')}
            options={modelOptions}
            required
            helperText={t('documentGeneration.generatePage.helperText.selectModel')}
            onChange={(e) => {
              if (e.target.value) {
                handleModelChange();
              }
            }}
          />

          {selectedModel ? (
            <>
              <EntityAutocompletes selectedModel={selectedModel} form={form} t={t} />
              <Box
                sx={{
                  mt: 2,
                }}
              >
                <AutocompleteField
                  name="documentType"
                  label={t('documentGeneration.generatePage.labels.selectDocumentType')}
                  options={documentTypeOptions}
                  required
                  helperText={t('documentGeneration.generatePage.helperText.selectDocumentType')}
                />
              </Box>
            </>
          ) : null}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2,
            }}
          >
            <Button type="submit" loading={isPending}>
              {t('documentGeneration.generatePage.actions.generateDocument')}
            </Button>
          </Box>
        </Form>
      </Section>
    </Page>
  );
};

const EntityAutocompletes = ({
  selectedModel,
  form,
  t,
}: {
  selectedModel: ContextModelNameEnum | null;
  form: ReturnType<typeof useForm<FormValues>>;
  t: I18nTranslateFunction<string>;
}) => {
  if (!selectedModel) return null;

  switch (selectedModel) {
    case ContextModelNameEnum.TradeCompany:
      return (
        <TradeCompanyAutocomplete
          name="entityId"
          label={t('documentGeneration.generatePage.labels.selectCompany')}
          required
          onSelectOption={(company) => {
            if (company) {
              form.setValue('entityId', company.id);
            }
          }}
        />
      );
    case ContextModelNameEnum.Deal:
      return (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <DealAutocomplete
            required
            onSelectDeal={(deal) => {
              if (deal) {
                form.setValue('entityId', deal.id);
              }
            }}
          />
        </Box>
      );
    case ContextModelNameEnum.Load:
      return (
        <LoadAutoComplete
          label={t('documentGeneration.generatePage.labels.selectLoad')}
          name="entityId"
          required
          onSelectOption={(load) => {
            if (load) {
              form.setValue('entityId', load.id);
            }
          }}
        />
      );
    case ContextModelNameEnum.Inspection:
      return (
        <InspectionAutocomplete
          name="entityId"
          label={t('documentGeneration.generatePage.labels.selectInspection')}
          required
          onSelectOption={(inspection) => {
            if (inspection) {
              form.setValue('entityId', inspection.id);
            }
          }}
        />
      );
    case ContextModelNameEnum.PurchaseOrder:
      return (
        <PurchaseOrderAutocomplete
          name="entityId"
          label={t('documentGeneration.generatePage.labels.selectPurchaseOrder')}
          required
          onSelectOption={(order) => {
            if (order) {
              form.setValue('entityId', order.id);
            }
          }}
        />
      );
    case ContextModelNameEnum.SalesOrder:
      return (
        <SalesOrderAutocomplete
          name="entityId"
          label={t('documentGeneration.generatePage.labels.selectSalesOrder')}
          required
          onSelectOption={(order) => {
            if (order) {
              form.setValue('entityId', order.id);
            }
          }}
        />
      );
    case ContextModelNameEnum.Shipment:
      return (
        <ShipmentAutocomplete
          name="entityId"
          label={t('documentGeneration.generatePage.labels.selectShipment')}
          required
          onSelectOption={(shipment) => {
            if (shipment) {
              form.setValue('entityId', shipment.id);
            }
          }}
        />
      );
    default:
      return null;
  }
};
